import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/shopify-produktseite-optimieren";
const TITLE = "Shopify Produktseite optimieren: Aufbau, der verkauft";
const DESCRIPTION =
  "Shopify Produktseite optimieren: Aufbau über dem ersten Scroll, Bilder, Preislogik und Einwandbehandlung, konkrete Reihenfolge statt Geschmack.";

const faqs = [
  {
    q: "Was gehört oben auf eine Shopify Produktseite?",
    a: "Produktname, ein Nutzen-Satz, Preis inklusive Versandhinweis, Verfügbarkeit und Lieferzeit sowie der Kaufbutton. Alles andere darf darunter stehen.",
  },
  {
    q: "Wie viele Produktbilder sind sinnvoll?",
    a: "Fünf bis acht reichen fast immer: Produkt freigestellt, im Einsatz, Detail, Größenvergleich, Verpackung. Wichtiger als die Menge ist, dass jedes Bild eine typische Kundenfrage beantwortet.",
  },
  {
    q: "Helfen Bewertungen wirklich?",
    a: "Ja, aber nur wenn sie konkret sind. Zehn ausführliche Rezensionen mit Fotos wirken stärker als 500 Sternebewertungen ohne Text.",
  },
  {
    q: "Sollte man Cross-Selling einbauen?",
    a: "Erst nach dem Kaufbutton oder im Warenkorb. Über dem Button lenkt jede Alternative von der Entscheidung ab, die Sie gerade herbeiführen wollen.",
  },
];

export const Route = createFileRoute("/ratgeber/shopify-produktseite-optimieren")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({ title: TITLE, description: DESCRIPTION, path: PATH, faqs }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Produktseite"
      title="Shopify Produktseite optimieren: Reihenfolge statt Geschmack"
      intro="Die Produktseite ist der Ort, an dem gekauft oder abgebrochen wird. Sie braucht keine besonders schöne Gestaltung, sondern eine Reihenfolge, die den Kunden Schritt für Schritt aus dem Zweifel führt."
      source="ratgeber-produktseite"
      sections={[
        {
          h: "Der Bereich über dem ersten Scroll",
          p: [
            "Hier entscheidet sich, ob überhaupt weitergelesen wird. Er muss drei Fragen beantworten: Was ist das, warum lohnt es sich für mich, und was kostet es mich am Ende wirklich.",
          ],
          list: [
            "Produktname plus ein konkreter Nutzen-Satz",
            "Preis mit Hinweis auf Versandkosten und Lieferzeit",
            "Verfügbarkeit und Lieferzeitraum in Tagen",
            "Ein einziger, deutlich sichtbarer Kaufbutton",
            "Ein Trust-Element: Rückgabe, Garantie oder Bewertung",
          ],
        },
        {
          h: "Bilder beantworten Fragen",
          p: [
            "Kunden lesen Produktbeschreibungen selten vollständig, Bilder schauen sie fast immer an. Deshalb gehört in die Bildstrecke, was sonst im Text untergeht: Größenverhältnis, Materialdetail, Anwendung im Alltag, Lieferumfang.",
          ],
        },
        {
          h: "Varianten und Optionen reduzieren",
          p: [
            "Jede zusätzliche gleichwertige Option verlängert die Entscheidung. Wenn eine Variante klar die beliebteste ist, sollte sie vorausgewählt und als Empfehlung markiert sein.",
          ],
        },
        {
          h: "Einwände vorwegnehmen",
          p: [
            "Sammle die fünf häufigsten Fragen aus Support und Retouren und beantworte sie direkt auf der Seite. Das reduziert nicht nur Abbrüche, sondern auch Supportaufwand und Rücksendungen.",
          ],
        },
        {
          h: "Was Sie danach messen sollten",
          p: [
            "Die relevante Kennzahl ist nicht die Verweildauer, sondern die Add-to-Cart-Rate der Seite und der Umsatz pro Session. Beides pro Top-Produkt getrennt betrachten, Durchschnittswerte verstecken die eigentlichen Problemseiten.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/blog/shopify-checkout-optimieren", label: "Checkout optimieren" },
        { to: "/ratgeber/shopify-conversion-optimierung", label: "Conversion-Leitfaden" },
        { to: "/kontakt", label: "Kostenlose Analyse" },
      ]}
    />
  );
}
